/**
 * Member care chat — context provider + hook.
 *
 * Two pieces:
 *  - <SupportChatProvider>: owns the `isOpen` + `sessionId` state and exposes
 *    open(sessionId?) / close() to descendants. Mounted once inside NavShell
 *    so every authenticated page can trigger the widget.
 *  - useSupportChat(): consumer hook used by EarningHistoryPage's
 *    "Contact support" button + the SupportChatWidget itself.
 *
 * Keeping the widget+provider split lets the floating button (in NavShell)
 * be the visual chrome while a remote consumer (the survey history row)
 * just calls `open(row.id)` without knowing where the widget renders.
 */
import { createContext, useCallback, useContext, useMemo, useState } from 'react';
import type { ReactNode } from 'react';

interface SupportChatContextValue {
  isOpen: boolean;
  sessionId: string | null;
  open: (sessionId?: string | null) => void;
  close: () => void;
}

const SupportChatContext = createContext<SupportChatContextValue | null>(null);

export function SupportChatProvider({ children }: { children: ReactNode }) {
  const [isOpen, setIsOpen] = useState(false);
  const [sessionId, setSessionId] = useState<string | null>(null);

  const open = useCallback((sid?: string | null) => {
    setSessionId(sid ?? null);
    setIsOpen(true);
  }, []);

  const close = useCallback(() => {
    setIsOpen(false);
    // Keep sessionId until the next open() call so a re-open during the
    // close-animation window doesn't lose the context.
  }, []);

  const value = useMemo<SupportChatContextValue>(
    () => ({ isOpen, sessionId, open, close }),
    [isOpen, sessionId, open, close],
  );

  return (
    <SupportChatContext.Provider value={value}>
      {children}
    </SupportChatContext.Provider>
  );
}

export function useSupportChat(): SupportChatContextValue {
  const ctx = useContext(SupportChatContext);
  if (!ctx) {
    // Render-outside-provider is almost certainly a bug — e.g. someone
    // forgot to wrap a top-level component. Surface it loudly rather
    // than handing back a no-op fallback that silently fails.
    throw new Error('useSupportChat must be used inside <SupportChatProvider>');
  }
  return ctx;
}
